/* ----------------------------------------------------------------------------
 * Aura Voice — typed network client for the server voice proxy (go-live wiring).
 *
 * SAFETY INVARIANT (non-negotiable): the browser NEVER holds a provider API
 * key. STT/TTS are reached ONLY through same-origin server proxy endpoints. STT
 * is a record-and-POST proxy: the browser uploads the recorded audio bytes and
 * receives a transcript (the server injects the provider key) — no token, no
 * WebSocket, no project id client-side. Nothing in this file constructs an
 * Authorization header or embeds a provider secret — the ONLY credential sent is
 * the session cookie (+ the CSRF double-submit token read from bc_csrf on mutations).
 *
 * WHY SELF-CONTAINED (not `import { apiRequest } from '@/lib/api'`): the voice
 * net tests run under Node's native `--test` (strip-only TS). `@/lib/api`
 * cannot load there — the `@/` alias is unresolved by Node and api.ts reads
 * `import.meta.env.VITE_API_BASE` at module load (throws under Node). So this
 * module faithfully REPLICATES api.ts's base-URL + credentials + CSRF +
 * error-envelope behaviour with a Node-safe base-URL accessor. Same wire
 * behaviour, testable offline.
 * -------------------------------------------------------------------------- */

/** Resolve the API base ('https://api.bookedcentral.com/v1' or a '/v1' proxy).
 *  Vite statically provides import.meta.env; Node's test runner does not, so we
 *  read defensively and fall back — never throwing at module load. */
import type { BriefingSection, NarrationSegment } from '../briefing/contract.ts';

function resolveApiBase(): string {
  try {
    const meta = import.meta as unknown as { env?: Record<string, string | undefined> };
    const fromEnv = meta.env ? meta.env.VITE_API_BASE : undefined;
    if (fromEnv) return fromEnv;
  } catch {
    /* Node --test: import.meta.env is absent — use the production default. */
  }
  return 'https://api.bookedcentral.com/v1';
}

const API_BASE = resolveApiBase();

/** Voice is fail-closed: a 503 `voice_unavailable` (flag off / key absent) maps
 *  to this typed error so callers keep Mock/disabled and never attempt a live
 *  call. */
export class VoiceUnavailableError extends Error {
  readonly code = 'voice_unavailable';
  readonly reason?: string;
  constructor(message?: string, reason?: string) {
    super(message ?? 'Voice is currently unavailable.');
    this.name = 'VoiceUnavailableError';
    this.reason = reason;
  }
}

/** Any other transport/protocol failure (network, non-503 HTTP, bad body). */
export class VoiceApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'VoiceApiError';
    this.status = status;
    this.code = code;
  }
}

// --- wire contract types (LOCKED — match the backend exactly) ---------------

export type VoiceProviderName = 'mock' | 'deepgram' | 'amazon';

export interface VoiceStatusResponse {
  ok: true;
  voice_available: boolean;
  stt_provider: VoiceProviderName | null;
  tts_provider: VoiceProviderName | null;
  tts_voice_id: string;
  reason?: string;
  /** ADDITIVE (Android voice diag, temporary): true when the server has
   *  AURA_VOICE_DIAG_ENABLED set, so the FE diagnostics beacon may activate.
   *  Absent/false on a normal server => the beacon stays a strict no-op. */
  diag?: boolean;
}

export interface SttTranscriptResponse {
  /** the transcribed utterance text. */
  transcript: string;
  /** provider confidence 0..1 (0 when not reported). */
  confidence: number;
  provider: string;
  model: string;
}

export interface TtsRequestOptions {
  voice?: string;
  speed?: number;
}

export interface BriefingResponse {
  briefing: Record<string, unknown>;
  narration?: string;
  /** ADDITIVE (2026-07-19): sectioned display + paced spoken segments. Optional
   *  for back-compat — absent on an older server, in which case the client uses
   *  the flat `narration` text. */
  sections?: BriefingSection[];
  narration_segments?: NarrationSegment[];
  provenance: { provider: 'Aura'; model: 'Copilot' };
}

// --- internals (mirror of api.ts, kept credential-safe) ---------------------

function readCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function newIdempotencyKey(): string | undefined {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* no-op */
  }
  return undefined;
}

function buildUrl(path: string): string {
  const base = API_BASE.replace(/\/$/, '');
  const rel = path.startsWith('/') ? path : `/${path}`;
  return `${base}${rel}`;
}

/** Headers for a mutating request: CSRF double-submit (from the bc_csrf cookie)
 *  + a fresh idempotency key. NEVER an Authorization header or provider key —
 *  auth is the session cookie carried by credentials:'include'. */
function mutationHeaders(json: boolean, accept: string): Record<string, string> {
  const headers: Record<string, string> = { Accept: accept };
  if (json) headers['Content-Type'] = 'application/json';
  const csrf = readCookie('bc_csrf');
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const idem = newIdempotencyKey();
  if (idem) headers['X-Bksesh-Idempotency-Key'] = idem;
  return headers;
}

function safeJson(text: string): Record<string, unknown> | undefined {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/** Map a non-ok JSON response to a typed error. 503 (or an envelope
 *  `voice_unavailable` code) becomes VoiceUnavailableError (fail-closed). */
function toTypedError(status: number, payload: Record<string, unknown> | undefined, fallbackMsg: string): Error {
  const nested =
    payload && typeof payload.error === 'object' && payload.error !== null
      ? (payload.error as Record<string, unknown>)
      : undefined;
  const code = (nested?.code as string) ?? (payload?.code as string) ?? 'error';
  const message = (nested?.message as string) ?? (payload?.message as string) ?? fallbackMsg;
  if (status === 503 || code === 'voice_unavailable') {
    return new VoiceUnavailableError(message, (nested?.reason as string) ?? (payload?.reason as string));
  }
  return new VoiceApiError(status, code, message);
}

async function jsonRequest<T>(
  path: string,
  method: 'GET' | 'POST',
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const isMutation = method !== 'GET';
  const headers = isMutation
    ? mutationHeaders(body !== undefined, 'application/json')
    : { Accept: 'application/json' };

  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      method,
      headers,
      credentials: 'include', // cookie-session — the ONLY credential
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (networkErr) {
    if ((networkErr as Error)?.name === 'AbortError') throw networkErr;
    throw new VoiceApiError(0, 'network_error', (networkErr as Error).message || 'Network request failed');
  }

  const text = await res.text();
  const payload = text ? safeJson(text) : undefined;
  if (!res.ok) throw toTypedError(res.status, payload, res.statusText);
  return payload as T;
}

// --- public client ----------------------------------------------------------

/** GET /v1/ai/voice/status — capability probe. Honest by design: the caller
 *  reads voice_available and stays Mock/disabled when false. */
export function getVoiceStatus(signal?: AbortSignal): Promise<VoiceStatusResponse> {
  return jsonRequest<VoiceStatusResponse>('/ai/voice/status', 'GET', undefined, signal);
}

/** POST /v1/ai/voice/diag — TEMPORARY diagnostics sink (Android voice retest).
 *  Fire-and-forget FE telemetry: same session cookie + CSRF as the other voice
 *  mutations. The server appends the event to evidence.log ONLY when
 *  AURA_VOICE_DIAG_ENABLED=1; otherwise it is a silent 204 no-op. Callers
 *  (voiceDiag.ts) already swallow errors — this must never disturb a turn. */
export async function postVoiceDiag(body: Record<string, unknown>, signal?: AbortSignal): Promise<void> {
  await jsonRequest<unknown>('/ai/voice/diag', 'POST', body, signal);
}

/** POST /v1/ai/voice/tts — returns the raw Response so the caller can stream
 *  the binary audio body (response.body reader) or buffer it (arrayBuffer).
 *  Aborts when `signal` fires (barge-in). 503 -> VoiceUnavailableError. */
export async function synthesizeTts(
  text: string,
  opts?: TtsRequestOptions,
  signal?: AbortSignal,
): Promise<Response> {
  const headers = mutationHeaders(true, 'audio/mpeg, audio/wav, application/octet-stream');
  const body: Record<string, unknown> = { text };
  if (opts?.voice) body.voice = opts.voice;
  if (typeof opts?.speed === 'number') body.speed = opts.speed;

  let res: Response;
  try {
    res = await fetch(buildUrl('/ai/voice/tts'), {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
      signal,
    });
  } catch (networkErr) {
    if ((networkErr as Error)?.name === 'AbortError') throw networkErr;
    throw new VoiceApiError(0, 'network_error', (networkErr as Error).message || 'Network request failed');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw toTypedError(res.status, text ? safeJson(text) : undefined, res.statusText);
  }
  return res;
}

/** POST /v1/ai/voice/stt — upload a recorded utterance; the server transcribes
 *  it (provider key injected server-side) and returns the transcript. The
 *  browser sends ONLY audio bytes + the session cookie + CSRF — never a provider
 *  key or token, no WebSocket, no project id. 503 -> VoiceUnavailableError. */
export async function transcribeStt(audio: Blob, signal?: AbortSignal): Promise<SttTranscriptResponse> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  headers['Content-Type'] = audio.type && audio.type !== '' ? audio.type : 'audio/webm';
  const csrf = readCookie('bc_csrf');
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const idem = newIdempotencyKey();
  if (idem) headers['X-Bksesh-Idempotency-Key'] = idem;
  // NON-BLOCKING DIAGNOSTICS (regression fix 2026-07-23): the per-capture cid is
  // deliberately NOT sent as an X-BC-Voice-Cid request header. STT is a
  // CROSS-ORIGIN POST, and a non-safelisted custom header forces a CORS preflight
  // that the server's FIXED allow-list (Content-Type, X-CSRF-Token,
  // X-Bksesh-Idempotency-Key) does NOT permit, so the browser BLOCKS the audio
  // POST (preflight 204, no POST) -> voice capture failed 5/5 on Android. A
  // diagnostics feature must NEVER break capture/send, so the header is removed.
  // The cid still rides the FE /voice/diag beacon; restoring the FE<->backend
  // JOIN needs a CORS-SAFE channel (query param or an allow-list update) and is
  // deferred as a backend follow-up.

  let res: Response;
  try {
    res = await fetch(buildUrl('/ai/voice/stt'), {
      method: 'POST',
      headers,
      credentials: 'include', // cookie-session — the ONLY credential
      body: audio,
      signal,
    });
  } catch (networkErr) {
    if ((networkErr as Error)?.name === 'AbortError') throw networkErr;
    throw new VoiceApiError(0, 'network_error', (networkErr as Error).message || 'Network request failed');
  }
  const text = await res.text();
  const payload = text ? safeJson(text) : undefined;
  if (!res.ok) throw toTypedError(res.status, payload, res.statusText);
  return payload as unknown as SttTranscriptResponse;
}

/** Once-per-session greeting signal for the briefing: the client flags the first
 *  spoken turn (greet) and supplies its LOCAL time (the server never guesses the
 *  zone from UTC). All fields optional — omitted => no greeting (graceful). */
export interface BriefingGreetingSignal {
  greet?: boolean;
  /** the client's wall-clock hour, 0-23. */
  localHour?: number;
  /** IANA timezone (e.g. 'Europe/London') as a fallback for localHour. */
  tz?: string;
}

/** POST /v1/ai/aura/briefing — daily briefing (advisory; persists nothing). */
export function fetchBriefing(
  greetingName?: string,
  greeting?: BriefingGreetingSignal,
  signal?: AbortSignal,
): Promise<BriefingResponse> {
  const body: Record<string, unknown> = {};
  if (greetingName) body.greeting_name = greetingName;
  if (greeting?.greet) body.greet = true;
  if (typeof greeting?.localHour === 'number') body.client_local_hour = greeting.localHour;
  if (greeting?.tz) body.client_tz = greeting.tz;
  return jsonRequest<BriefingResponse>('/ai/aura/briefing', 'POST', body, signal);
}
